/*
 * Module dependencies.
 */

// https://github.com/thirdpartyjs/thirdpartyjs-code/blob/master/examples/templates/02/loading-files/index.html

/**
 * Invoke `fn(err)` when the given `el` script loads.
 *
 * @param {Element} el
 * @param {Function} fn
 * @api public
 */
function onload(el, fn) {
  return el.addEventListener ? add(el, fn) : attach(el, fn);
}

/**
 * Add event listener to `el`, `fn()`.
 *
 * @param {Element} el
 * @param {Function} fn
 * @api private
 */
function add(el, fn) {
  el.addEventListener(
    "load",
    (_, e) => {
      fn(null, e);
    },
    false
  );
  el.addEventListener(
    "error",
    e => {
      const err = new Error(`script error "${el.src}"`);
      err.event = e;
      fn(err);
    },
    false
  );
}

/**
 * Attach event.
 *
 * @param {Element} el
 * @param {Function} fn
 * @api private
 */
function attach(el, fn) {
  el.attachEvent("onreadystatechange", e => {
    if (!/complete|loaded/.test(el.readyState)) {
      return;
    }
    fn(null, e);
  });
  el.attachEvent("onerror", e => {
    const err = new Error(`failed to load the script "${el.src}"`);
    err.event = e || window.event;
    fn(err);
  });
}

/**
 * Loads a script asynchronously.
 *
 * @param {Object} options
 * @param {Function} cb
 */
export default function loadScript(options, cb) {
  if (!options) {
    throw new Error("Can't load nothing...");
  }

  // Allow for the simplest case, just passing a `src` string.
  if (typeof options === "string") {
    options = { src: options };
  }

  const https =
    document.location.protocol === "https:" ||
    document.location.protocol === "chrome-extension:";

  // If you use protocol relative URLs, third-party scripts like Google
  // Analytics break when testing with `file:` so this fixes that.
  if (options.src && options.src.indexOf("//") === 0) {
    options.src = (https ? "https:" : "http:") + options.src;
  }

  // Make the `<script>` element and insert it before the first script on the
  // page, which is guaranteed to exist since this Javascript is running.
  const script = document.createElement("script");
  script.type = "text/javascript";
  script.async = true;
  script.src = options.src;

  for (const key in options) {
    if (options.hasOwnProperty(key) && key !== "src") {
      script.setAttribute(key, options[key]);
    }
  }

  // If we have a cb, attach event handlers. Does not work on < IE9 because
  // older browser versions don't register element.onerror
  if (typeof cb === "function") {
    onload(script, cb);
  }

  setTimeout(() => {
    // Append after event listeners are attached for IE.
    const firstScript = document.getElementsByTagName("script")[0];
    firstScript.parentNode.insertBefore(script, firstScript);
  }, 0);

  // Return the script element in case they want to do anything special, like
  // give it an ID or attributes.
  return script;
}
